import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { getCachedSignedUrl } from '../utils/storage';

const getYoutubeVideoId = (rawUrl) => {
  const url = String(rawUrl || '').trim();
  if (!url) return '';
  const directId = url.match(/^[a-zA-Z0-9_-]{11}$/)?.[0];
  if (directId) return directId;
  try {
    const u = new URL(url);
    const host = (u.hostname || '').replace(/^www\./, '');
    if (host === 'youtu.be') return (u.pathname || '').replace('/', '').slice(0, 11);
    const v = u.searchParams.get('v');
    if (v) return v.slice(0, 11);
    const m = (u.pathname || '').match(/\/shorts\/([^/?#]+)/) || (u.pathname || '').match(/\/embed\/([^/?#]+)/);
    if (m?.[1]) return String(m[1]).slice(0, 11);
    return '';
  } catch {
    return '';
  }
};

const getYoutubeThumb = (rawUrl) => {
  const id = getYoutubeVideoId(rawUrl);
  if (!id) return '';
  return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
};

const buildMonthWeeks = (baseDate) => {
  const year = baseDate.getFullYear();
  const month = baseDate.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const lastOfMonth = new Date(year, month + 1, 0);
  const start = new Date(firstOfMonth);
  start.setDate(start.getDate() - start.getDay());

  const weeks = [];
  let cursor = new Date(start);
  while (cursor <= lastOfMonth) {
    const weekStart = new Date(cursor);
    const weekEnd = new Date(cursor);
    weekEnd.setDate(weekEnd.getDate() + 6);
    weeks.push({ start: weekStart, end: weekEnd });
    cursor.setDate(cursor.getDate() + 7);
  }

  const currentWeekIndex = weeks.findIndex((w) => baseDate >= w.start && baseDate <= w.end);

  return {
    year,
    month,
    weeks,
    currentWeek: Math.max(1, currentWeekIndex + 1),
    tabs: weeks.map((_, idx) => idx + 1),
    firstOfMonth,
    lastOfMonth,
  };
};

const computeStreaks = (dates) => {
  const uniqueDays = Array.from(
    new Set(
      (dates || [])
        .filter(Boolean)
        .map((d) => new Date(d).toISOString().slice(0, 10))
    )
  ).sort();

  if (uniqueDays.length === 0) {
    return { currentStreak: 0, longestStreak: 0, lastActivityDate: null };
  }

  const asDayNumber = (isoDay) => Math.floor(new Date(`${isoDay}T00:00:00Z`).getTime() / 86400000);
  const dayNums = uniqueDays.map(asDayNumber);

  let longest = 1;
  let currentRun = 1;
  for (let i = 1; i < dayNums.length; i += 1) {
    if (dayNums[i] === dayNums[i - 1] + 1) {
      currentRun += 1;
    } else {
      longest = Math.max(longest, currentRun);
      currentRun = 1;
    }
  }
  longest = Math.max(longest, currentRun);

  let currentStreak = 1;
  for (let i = dayNums.length - 1; i > 0; i -= 1) {
    if (dayNums[i] === dayNums[i - 1] + 1) currentStreak += 1;
    else break;
  }

  return {
    currentStreak,
    longestStreak: longest,
    lastActivityDate: `${uniqueDays[uniqueDays.length - 1]}T00:00:00Z`,
  };
};

const getLevelInfo = (scoreValue) => {
  const thresholds = [0, 100, 300, 600, 1000];
  let level = 1;
  let current = 0;
  let next = 100;

  for (let i = thresholds.length - 1; i >= 0; i -= 1) {
    if (scoreValue >= thresholds[i]) {
      level = i + 1;
      current = thresholds[i];
      next = thresholds[i + 1] ?? (thresholds[i] + 500);
      break;
    }
  }

  const progress = next > current
    ? Math.min(100, ((scoreValue - current) / (next - current)) * 100)
    : 100;

  return { level, current, next, progress };
};

const formatDuration = (minutesValue) => {
  const minutes = Number(minutesValue) || 0;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours && rest) return `${hours}시간 ${rest}분`;
  if (hours) return `${hours}시간`;
  return `${rest}분`;
};

export const useUserProfileDashboardData = ({ userId }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [overviewTab, setOverviewTab] = useState('goals');

  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const observerTarget = useRef(null);

  const [myNeighbors, setMyNeighbors] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [isNeighborsLoading, setIsNeighborsLoading] = useState(false);

  const [achievementCategory, setAchievementCategory] = useState('all');

  const PAGE_SIZE = 12;
  const [cursor, setCursor] = useState({ offset: 0 });

  const [statistics, setStatistics] = useState({
    totalProjects: 0,
    completedChallenges: 0,
    submittedProofs: 0,
    averageStars: 0,
    receivedLikes: 0,
  });
  const [score, setScore] = useState(0);

  const [starDistribution, setStarDistribution] = useState([
    { stars: 5, count: 0 },
    { stars: 4, count: 0 },
    { stars: 3, count: 0 },
    { stars: 2, count: 0 },
    { stars: 1, count: 0 },
  ]);

  const [recentProofs, setRecentProofs] = useState([]);
  const [achievementBadges, setAchievementBadges] = useState([]);
  const [streakData, setStreakData] = useState({ currentStreak: 0, longestStreak: 0, lastActivityDate: null });
  const [progressTrendData, setProgressTrendData] = useState([]);
  const [trendMode, setTrendMode] = useState('monthly');
  const [trendYear, setTrendYear] = useState(new Date().getUTCFullYear());
  const [trendWeekIndex, setTrendWeekIndex] = useState(1);

  const [destimeStats, setDestimeStats] = useState({ todayMinutes: 0, weekMinutes: 0, monthMinutes: 0 });
  const [destimeTopActions, setDestimeTopActions] = useState([]);
  const [destimeEntryDates, setDestimeEntryDates] = useState([]);
  const [destimeEntrySamples, setDestimeEntrySamples] = useState([]);
  const [destimeTrendMode, setDestimeTrendMode] = useState('monthly');
  const [destimeTrendYear, setDestimeTrendYear] = useState(new Date().getUTCFullYear());
  const [destimeTrendWeekIndex, setDestimeTrendWeekIndex] = useState(1);

  const [allProofDates, setAllProofDates] = useState([]);

  const [isProofDetailOpen, setIsProofDetailOpen] = useState(false);
  const [activeProofIndex, setActiveProofIndex] = useState(0);

  const weeklyTabs = useMemo(() => buildMonthWeeks(new Date()), []);
  const destimeWeeklyTabs = useMemo(() => buildMonthWeeks(new Date()), []);

  const safeTrendValue = useMemo(() => {
    return {
      projects: String(statistics?.totalProjects || 0),
      challenges: String(statistics?.completedChallenges || 0),
      proofs: String(statistics?.submittedProofs || 0),
      avg: String(statistics?.averageStars || 0),
    };
  }, [statistics]);

  const availableTrendYears = useMemo(() => {
    const years = new Set((allProofDates || []).map((d) => new Date(d).getUTCFullYear()));
    const arr = Array.from(years).sort((a, b) => b - a);
    return arr.length > 0 ? arr : [new Date().getUTCFullYear()];
  }, [allProofDates]);

  const destimeAvailableYears = useMemo(() => {
    const years = new Set((destimeEntrySamples || []).map((s) => new Date(s?.started_at).getUTCFullYear()));
    const arr = Array.from(years).sort((a, b) => b - a);
    return arr.length > 0 ? arr : [new Date().getUTCFullYear()];
  }, [destimeEntrySamples]);

  useEffect(() => {
    if (!availableTrendYears.includes(trendYear)) {
      setTrendYear(availableTrendYears[0] || new Date().getUTCFullYear());
    }
  }, [availableTrendYears, trendYear]);

  useEffect(() => {
    if (!destimeAvailableYears.includes(destimeTrendYear)) {
      setDestimeTrendYear(destimeAvailableYears[0] || new Date().getUTCFullYear());
    }
  }, [destimeAvailableYears, destimeTrendYear]);

  useEffect(() => {
    if (trendWeekIndex > weeklyTabs.currentWeek) setTrendWeekIndex(weeklyTabs.currentWeek || 1);
    if (trendWeekIndex < 1) setTrendWeekIndex(1);
  }, [trendWeekIndex, weeklyTabs.currentWeek]);

  useEffect(() => {
    if (destimeTrendWeekIndex > destimeWeeklyTabs.currentWeek) setDestimeTrendWeekIndex(destimeWeeklyTabs.currentWeek || 1);
    if (destimeTrendWeekIndex < 1) setDestimeTrendWeekIndex(1);
  }, [destimeTrendWeekIndex, destimeWeeklyTabs.currentWeek]);

  const monthlyTrendDataByYear = useMemo(() => {
    const buckets = new Array(12).fill(0);
    (allProofDates || []).forEach((d) => {
      const dt = new Date(d);
      if (dt.getUTCFullYear() !== trendYear) return;
      const m = dt.getUTCMonth();
      if (m >= 0 && m < 12) buckets[m] += 1;
    });
    return buckets.map((count, idx) => ({ month: `${idx + 1}월`, proofs: count }));
  }, [allProofDates, trendYear]);

  const weeklyTrendData = useMemo(() => {
    const targetWeek = weeklyTabs.weeks?.[trendWeekIndex - 1];
    if (!targetWeek) return [];
    const today = new Date();
    const isCurrentMonth = today.getFullYear() === weeklyTabs.year && today.getMonth() === weeklyTabs.month;
    const safeEnd = isCurrentMonth && today < targetWeek.end ? today : targetWeek.end;

    const dayCounts = new Map();
    (allProofDates || []).forEach((d) => {
      const dt = new Date(d);
      if (dt < targetWeek.start || dt > safeEnd) return;
      if (dt.getMonth() !== weeklyTabs.month || dt.getFullYear() !== weeklyTabs.year) return;
      const day = dt.getDate();
      dayCounts.set(day, (dayCounts.get(day) || 0) + 1);
    });

    const data = [];
    const cursorDate = new Date(targetWeek.start);
    while (cursorDate <= safeEnd) {
      if (cursorDate.getMonth() === weeklyTabs.month) {
        const day = cursorDate.getDate();
        data.push({ day: `${day}일`, proofs: dayCounts.get(day) || 0 });
      }
      cursorDate.setDate(cursorDate.getDate() + 1);
    }
    return data;
  }, [allProofDates, trendWeekIndex, weeklyTabs]);

  const destimeMonthlyTrendData = useMemo(() => {
    const buckets = new Array(12).fill(0);
    (destimeEntrySamples || []).forEach((s) => {
      const dt = new Date(s?.started_at);
      if (dt.getUTCFullYear() !== destimeTrendYear) return;
      const m = dt.getUTCMonth();
      if (m >= 0 && m < 12) buckets[m] += Number(s?.minutes || 0);
    });
    return buckets.map((count, idx) => ({ month: `${idx + 1}월`, minutes: count }));
  }, [destimeEntrySamples, destimeTrendYear]);

  const destimeWeeklyTrendData = useMemo(() => {
    const targetWeek = destimeWeeklyTabs.weeks?.[destimeTrendWeekIndex - 1];
    if (!targetWeek) return [];
    const today = new Date();
    const isCurrentMonth = today.getFullYear() === destimeWeeklyTabs.year && today.getMonth() === destimeWeeklyTabs.month;
    const safeEnd = isCurrentMonth && today < targetWeek.end ? today : targetWeek.end;

    const dayMinutes = new Map();
    (destimeEntrySamples || []).forEach((s) => {
      const dt = new Date(s?.started_at);
      if (dt < targetWeek.start || dt > safeEnd) return;
      if (dt.getMonth() !== destimeWeeklyTabs.month || dt.getFullYear() !== destimeWeeklyTabs.year) return;
      const day = dt.getDate();
      dayMinutes.set(day, (dayMinutes.get(day) || 0) + Number(s?.minutes || 0));
    });

    const data = [];
    const cursorDate = new Date(targetWeek.start);
    while (cursorDate <= safeEnd) {
      if (cursorDate.getMonth() === destimeWeeklyTabs.month) {
        const day = cursorDate.getDate();
        data.push({ day: `${day}일`, minutes: dayMinutes.get(day) || 0 });
      }
      cursorDate.setDate(cursorDate.getDate() + 1);
    }
    return data;
  }, [destimeEntrySamples, destimeTrendWeekIndex, destimeWeeklyTabs]);

  const lastAttendanceText = useMemo(() => {
    if (!streakData.lastActivityDate) return '기록 없음';
    return new Date(streakData.lastActivityDate).toLocaleDateString('ko-KR');
  }, [streakData.lastActivityDate]);

  const levelInfo = useMemo(() => getLevelInfo(score), [score]);

  const openProofDetailById = useCallback(
    (proofId) => {
      const idx = recentProofs?.findIndex((p) => p?.id === proofId);
      if (idx < 0) return;
      setActiveProofIndex(idx);
      setIsProofDetailOpen(true);
    },
    [recentProofs]
  );

  const handlePrevProof = useCallback(() => {
    setActiveProofIndex((i) => Math.max(0, i - 1));
  }, []);

  const handleNextProof = useCallback(() => {
    setActiveProofIndex((i) => Math.min((recentProofs?.length || 1) - 1, i + 1));
  }, [recentProofs?.length]);

  const categorizeAchievement = useCallback((criteria) => {
    const type = criteria?.type;
    if (type === 'proof_count' || type === 'completed_challenge_count') return 'proofs';
    if (type === 'streak_days') return 'streak';
    if (type === 'five_star_count' || type === 'avg_star_gte') return 'stars';
    if (type === 'project_count') return 'projects';
    if (type === 'public_proof_count' || type === 'private_proof_count') return 'visibility';
    if (type === 'like_given_count' || type === 'like_received_count') return 'recommendation';
    if (type === 'destime_entry_count' || type === 'destime_total_minutes' || type === 'destime_active_days') return 'destime';
    return 'others';
  }, []);

  const achievementFilters = useMemo(
    () => ([
      { id: 'all', label: '전체' },
      { id: 'proofs', label: '증명' },
      { id: 'streak', label: '연속' },
      { id: 'stars', label: '별점' },
      { id: 'projects', label: '목표' },
      { id: 'visibility', label: '공개/비공개' },
      { id: 'recommendation', label: '추천' },
      { id: 'destime', label: '데스타임' },
      { id: 'others', label: '기타' },
    ]),
    []
  );

  const filteredBadges = useMemo(() => {
    if (achievementCategory === 'all') return achievementBadges;
    return achievementBadges.filter((b) => categorizeAchievement(b?.criteria) === achievementCategory);
  }, [achievementBadges, achievementCategory, categorizeAchievement]);

  const unlockedBadges = useMemo(() => filteredBadges.filter((b) => b?.unlocked), [filteredBadges]);
  const lockedBadges = useMemo(() => filteredBadges.filter((b) => !b?.unlocked), [filteredBadges]);

  const loadNeighborManagement = useCallback(async () => {
    if (!userId) return;
    setIsNeighborsLoading(true);
    try {
      const { data: neighborsRows } = await supabase.rpc('ch_list_my_neighbors');
      const { data: blockedRows } = await supabase.rpc('ch_list_blocked_users');

      const mappedNeighbors = (neighborsRows || []).map((n) => ({
        id: n.neighbor_id,
        username: n.username,
        avatarUrl: n.avatar_url || '',
        createdAt: n.created_at,
      }));

      const mappedBlocked = (blockedRows || []).map((b) => ({
        id: b.blocked_id,
        username: b.username,
        avatarUrl: b.avatar_url || '',
        createdAt: b.created_at,
      }));

      setMyNeighbors(mappedNeighbors);
      setBlockedUsers(mappedBlocked);
    } finally {
      setIsNeighborsLoading(false);
    }
  }, [userId]);

  const removeNeighbor = useCallback(async (neighborId) => {
    if (!neighborId) return;
    await supabase.rpc('ch_toggle_neighbor', { p_neighbor_id: neighborId });
    await loadNeighborManagement();
  }, [loadNeighborManagement]);

  const blockNeighbor = useCallback(async (neighborId) => {
    if (!neighborId) return;
    await supabase.rpc('ch_block_user', { p_blocked_id: neighborId });
    await loadNeighborManagement();
  }, [loadNeighborManagement]);

  const unblockUser = useCallback(async (blockedId) => {
    if (!blockedId) return;
    await supabase.rpc('ch_unblock_user', { p_blocked_id: blockedId });
    await loadNeighborManagement();
  }, [loadNeighborManagement]);

  const loadDashboardData = useCallback(async () => {
    if (!userId) return;

    setIsLoading(true);

    const [
      { count: projectCount },
      { data: proofsAll, error: proofsErr },
      { data: challengesAll },
      { data: receivedLikesCount },
      { data: givenLikesCount },
      { data: destimeEntries },
      { data: profileRow },
    ] = await Promise.all([
      supabase.from('ch_projects').select('id', { count: 'exact', head: true }).eq('owner_id', userId),
      supabase
        .from('ch_proofs')
        .select('id, rating, is_public, submitted_at, updated_at, challenge_id')
        .eq('user_id', userId)
        .order('submitted_at', { ascending: false }),
      supabase
        .from('ch_challenges')
        .select('id, total_goals, target_count, goal_type')
        .eq('owner_id', userId),
      supabase.rpc('ch_get_my_received_likes_count'),
      supabase.rpc('ch_get_my_given_likes_count'),
      supabase
        .from('dt_time_entries')
        .select('*')
        .eq('user_id', userId)
        .eq('source_type', 'import_proof')
        .order('started_at', { ascending: false }),
      supabase
        .from('ch_profiles')
        .select('score')
        .eq('id', userId)
        .single(),
    ]);

    if (proofsErr) {
      setIsLoading(false);
      return;
    }

    const proofs = proofsAll || [];
    setAllProofDates((proofs || []).map((p) => p?.submitted_at).filter(Boolean));
    const proofCount = proofs.length;
    const totalStars = proofs.reduce((sum, p) => sum + Number(p.rating || 0), 0);
    const avgStars = proofCount > 0 ? totalStars / proofCount : 0;
    const fiveStarCount = proofs.filter((p) => Number(p.rating || 0) === 5).length;
    const publicProofCount = proofs.filter((p) => p.is_public).length;
    const privateProofCount = proofs.filter((p) => !p.is_public).length;
    const proofEditCount = proofs.filter((p) => p.updated_at && p.submitted_at && p.updated_at !== p.submitted_at).length;

    const activeMonths = new Set(
      proofs
        .map((p) => p.submitted_at)
        .filter(Boolean)
        .map((d) => {
          const dt = new Date(d);
          return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}`;
        })
    ).size;

    const nightProofCount = proofs.filter((p) => {
      if (!p.submitted_at) return false;
      const h = new Date(p.submitted_at).getHours();
      return h >= 21 || h <= 3;
    }).length;

    const morningProofCount = proofs.filter((p) => {
      if (!p.submitted_at) return false;
      const h = new Date(p.submitted_at).getHours();
      return h >= 5 && h <= 9;
    }).length;

    const streak = computeStreaks(proofs.map((p) => p.submitted_at));
    setStreakData(streak);

    const distribution = [5, 4, 3, 2, 1].map((s) => ({
      stars: s,
      count: proofs.filter((p) => Number(p.rating || 0) === s).length,
    }));
    setStarDistribution(distribution);

    const monthBuckets = new Map();
    proofs.forEach((p) => {
      if (!p.submitted_at) return;
      const dt = new Date(p.submitted_at);
      const key = `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}`;
      monthBuckets.set(key, (monthBuckets.get(key) || 0) + 1);
    });
    const last6 = Array.from(monthBuckets.entries())
      .sort(([a], [b]) => (a > b ? 1 : -1))
      .slice(-6)
      .map(([key, count]) => {
        const [, mm] = key.split('-');
        return { month: `${Number(mm)}월`, proofs: count, challenges: 0 };
      });
    setProgressTrendData(last6);

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    const resolvedDestime = await Promise.all(
      (destimeEntries || []).map(async (entry) => {
        if (entry.icon_url) {
          return { ...entry, iconUrl: entry.icon_url };
        }
        if (entry.icon_type !== 'custom' || !entry.icon_path) {
          return { ...entry, iconUrl: '' };
        }
        const iconUrl = await getCachedSignedUrl({ bucket: 'destime-icons', path: entry.icon_path });
        return { ...entry, iconUrl };
      })
    );

    let todayMinutes = 0;
    let weekMinutes = 0;
    let monthMinutes = 0;
    let totalDestimeMinutes = 0;
    const destimeActiveDaySet = new Set();
    const actionMap = new Map();

    const samples = [];

    resolvedDestime.forEach((entry) => {
      const startedAt = new Date(entry.started_at);
      const durationMinutes = entry.duration_minutes
        ?? Math.max(0, Math.round((new Date(entry.ended_at) - new Date(entry.started_at)) / 60000));

      totalDestimeMinutes += durationMinutes;
      if (entry.started_at) {
        const isoDay = new Date(entry.started_at).toISOString().slice(0, 10);
        destimeActiveDaySet.add(isoDay);
      }

      if (entry.started_at) {
        samples.push({ started_at: entry.started_at, minutes: durationMinutes });
      }

      if (startedAt >= startOfToday) todayMinutes += durationMinutes;
      if (startedAt >= startOfWeek) weekMinutes += durationMinutes;
      if (startedAt >= startOfMonth) monthMinutes += durationMinutes;

      const key = `${entry.title || '기록'}|${entry.iconUrl || entry.icon_name || entry.icon_path || ''}`;
      const existing = actionMap.get(key) || {
        title: entry.title || '기록',
        iconUrl: entry.iconUrl || '',
        iconName: entry.icon_name || 'Timer',
        minutes: 0,
      };
      existing.minutes += durationMinutes;
      actionMap.set(key, existing);
    });

    setDestimeStats({ todayMinutes, weekMinutes, monthMinutes });
    setDestimeEntryDates(resolvedDestime.map((entry) => entry.started_at).filter(Boolean));
    setDestimeEntrySamples(samples);

    const ranked = Array.from(actionMap.values())
      .sort((a, b) => b.minutes - a.minutes)
      .slice(0, 5);
    setDestimeTopActions(ranked);

    const challenges = challengesAll || [];
    const { data: proofCountsByChallenge } = await supabase
      .from('ch_proofs')
      .select('challenge_id')
      .eq('user_id', userId);

    const cntMap = new Map();
    (proofCountsByChallenge || []).forEach((r) => {
      cntMap.set(r.challenge_id, (cntMap.get(r.challenge_id) || 0) + 1);
    });

    const completedChallengeCount = challenges.filter((c) => {
      const goalType = c.goal_type === 'thirty-day' ? 'thirty_day' : c.goal_type;
      const total = goalType === 'thirty_day'
        ? 30
        : Number(c.total_goals ?? c.target_count ?? 0);
      const done = cntMap.get(c.id) || 0;
      return total > 0 && done >= total;
    }).length;

    setStatistics({
      totalProjects: projectCount || 0,
      completedChallenges: completedChallengeCount,
      submittedProofs: proofCount,
      averageStars: Number(avgStars.toFixed(1)),
      receivedLikes: Number(receivedLikesCount || 0),
      receivedLikesRaw: Number(receivedLikesCount || 0),
      givenLikesRaw: Number(givenLikesCount || 0),
    });
    setScore(Number(profileRow?.score || 0));

    setCursor({ offset: 0 });
    setRecentProofs([]);
    setHasMore(true);

    const ctx = {
      proofCount,
      projectCount: projectCount || 0,
      completedChallengeCount,
      fiveStarCount,
      avgStars,
      currentStreak: streak.currentStreak,
      publicProofCount,
      privateProofCount,
      activeMonths,
      proofEditCount,
      nightProofCount,
      morningProofCount,
      likeGivenCount: Number(givenLikesCount || 0),
      likeReceivedCount: Number(receivedLikesCount || 0),
      destimeEntryCount: resolvedDestime.length,
      destimeTotalMinutes: totalDestimeMinutes,
      destimeActiveDays: destimeActiveDaySet.size,
    };

    const { data: allAchievements } = await supabase
      .from('ch_achievements')
      .select('id, code, name, description, criteria, icon_name')
      .order('created_at', { ascending: true });

    const { data: unlocked } = await supabase
      .from('ch_user_achievements')
      .select('achievement_id, unlocked_at')
      .eq('user_id', userId);

    const unlockedMap = new Map((unlocked || []).map((u) => [u.achievement_id, u.unlocked_at]));

    const evaluateAchievement = (criteria) => {
      const type = criteria?.type;
      const threshold = criteria?.threshold;

      if (type === 'proof_count') return (ctx?.proofCount || 0) >= threshold;
      if (type === 'project_count') return (ctx?.projectCount || 0) >= threshold;
      if (type === 'completed_challenge_count') return (ctx?.completedChallengeCount || 0) >= threshold;
      if (type === 'five_star_count') return (ctx?.fiveStarCount || 0) >= threshold;
      if (type === 'avg_star_gte') return (ctx?.avgStars || 0) >= threshold;
      if (type === 'streak_days') return (ctx?.currentStreak || 0) >= threshold;
      if (type === 'public_proof_count') return (ctx?.publicProofCount || 0) >= threshold;
      if (type === 'private_proof_count') return (ctx?.privateProofCount || 0) >= threshold;
      if (type === 'active_months') return (ctx?.activeMonths || 0) >= threshold;
      if (type === 'proof_edit_count') return (ctx?.proofEditCount || 0) >= threshold;
      if (type === 'night_proof_count') return (ctx?.nightProofCount || 0) >= threshold;
      if (type === 'morning_proof_count') return (ctx?.morningProofCount || 0) >= threshold;
      if (type === 'like_given_count') return (ctx?.likeGivenCount || 0) >= threshold;
      if (type === 'like_received_count') return (ctx?.likeReceivedCount || 0) >= threshold;
      if (type === 'destime_entry_count') return (ctx?.destimeEntryCount || 0) >= threshold;
      if (type === 'destime_total_minutes') return (ctx?.destimeTotalMinutes || 0) >= threshold;
      if (type === 'destime_active_days') return (ctx?.destimeActiveDays || 0) >= threshold;

      return false;
    };

    const toUnlock = (allAchievements || [])
      .filter((a) => !unlockedMap.has(a.id) && evaluateAchievement(a.criteria))
      .map((a) => ({ user_id: userId, achievement_id: a.id }));

    const toLock = (allAchievements || [])
      .filter((a) => unlockedMap.has(a.id) && !evaluateAchievement(a.criteria))
      .map((a) => a.id);

    if (toUnlock.length > 0) {
      await supabase.from('ch_user_achievements').insert(toUnlock);
    }

    if (toLock.length > 0) {
      await supabase.from('ch_user_achievements').delete().in('achievement_id', toLock).eq('user_id', userId);
      toLock.forEach((achievementId) => unlockedMap.delete(achievementId));
    }

    const badges = (allAchievements || []).map((a) => ({
      id: a.id,
      code: a.code,
      name: a.name,
      description: a.description,
      criteria: a.criteria,
      iconName: a.icon_name,
      unlockedAt: unlockedMap.get(a.id) || null,
      unlocked: unlockedMap.has(a.id),
    }));
    setAchievementBadges(badges);

    setIsLoading(false);
  }, [userId]);

  const loadMoreProofs = useCallback(async () => {
    if (!userId || isLoading || !hasMore) return;

    setIsLoading(true);

    const from = cursor.offset;
    const to = cursor.offset + PAGE_SIZE - 1;
    const { data, error } = await supabase
      .from('ch_proofs')
      .select('id, number, title, description, rating, is_public, submitted_at, image_path, youtube_url, challenge:ch_challenges(id, title, project:ch_projects(title))')
      .eq('user_id', userId)
      .order('submitted_at', { ascending: false })
      .range(from, to);

    if (error) {
      setIsLoading(false);
      return;
    }

    const next = await Promise.all(
      (data || []).map(async (p) => {
        const thumb = p.youtube_url ? getYoutubeThumb(p.youtube_url) : '';
        const url = p.image_path
          ? await getCachedSignedUrl({ bucket: 'proof-images', path: p.image_path })
          : (thumb || '');
        return {
          id: p.id,
          challengeName: p.challenge?.title || '',
          challengeId: p.challenge?.id || '',
          projectName: p.challenge?.project?.title || '',
          image: url,
          imageAlt: p.challenge?.title ? `Proof image for ${p.challenge.title}` : '',
          stars: Number(p.rating || 0),
          proofNumber: p.number,
          submittedAt: p.submitted_at,
          isPublic: !!p.is_public,
          title: p.title || '',
          description: p.description || '',
          youtubeUrl: p.youtube_url || '',
        };
      })
    );

    setRecentProofs((prev) => [...prev, ...next]);
    setCursor((prev) => ({ offset: prev.offset + PAGE_SIZE }));
    if ((data || []).length < PAGE_SIZE) setHasMore(false);
    setIsLoading(false);
  }, [PAGE_SIZE, cursor.offset, hasMore, isLoading, userId]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  useEffect(() => {
    if (!userId) return undefined;

    const channel = supabase
      .channel(`dt_time_entries_dashboard_${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'dt_time_entries',
          filter: `user_id=eq.${userId}`,
        },
        async () => {
          await loadDashboardData();
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          loadDashboardData();
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [loadDashboardData, userId]);

  useEffect(() => {
    const onFocus = () => {
      loadDashboardData();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') loadDashboardData();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [loadDashboardData]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          loadMoreProofs();
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget?.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget?.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, isLoading, loadMoreProofs]);

  useEffect(() => {
    if (!userId) return;
    if (recentProofs.length === 0 && hasMore && !isLoading) {
      loadMoreProofs();
    }
  }, [hasMore, isLoading, loadMoreProofs, recentProofs.length, userId]);

  useEffect(() => {
    if (activeTab !== 'neighbors') return;
    loadNeighborManagement();
  }, [activeTab, loadNeighborManagement]);

  return {
    activeTab,
    setActiveTab,
    overviewTab,
    setOverviewTab,

    isLoading,
    hasMore,
    observerTarget,

    statistics,
    score,
    levelInfo,

    starDistribution,
    streakData,
    lastAttendanceText,

    progressTrendData,
    trendMode,
    setTrendMode,
    trendYear,
    setTrendYear,
    weeklyTabs,
    trendWeekIndex,
    setTrendWeekIndex,
    monthlyTrendDataByYear,
    weeklyTrendData,
    availableTrendYears,
    safeTrendValue,

    destimeStats,
    destimeTopActions,
    destimeEntryDates,
    destimeEntrySamples,
    destimeTrendMode,
    setDestimeTrendMode,
    destimeTrendYear,
    setDestimeTrendYear,
    destimeWeeklyTabs,
    destimeTrendWeekIndex,
    setDestimeTrendWeekIndex,
    destimeMonthlyTrendData,
    destimeWeeklyTrendData,
    destimeAvailableYears,

    recentProofs,
    openProofDetailById,
    isProofDetailOpen,
    setIsProofDetailOpen,
    activeProofIndex,
    setActiveProofIndex,
    handlePrevProof,
    handleNextProof,

    achievementFilters,
    achievementCategory,
    setAchievementCategory,
    unlockedBadges,
    lockedBadges,

    myNeighbors,
    blockedUsers,
    isNeighborsLoading,
    loadNeighborManagement,
    removeNeighbor,
    blockNeighbor,
    unblockUser,

    formatDuration,
  };
};

export default useUserProfileDashboardData;
